import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { CheckIcon, CheckOnlyIcon, RefreshIcon } from "../Auth/onboarding/icons";
import { getInstallationStatus } from "../../actions/assistants";
import { hostOf } from "../../utils/url";
import CardHeading from "./CardHeading";

// The crawl states that mean "still working".
const PENDING_STATUSES = ["crawling", "pending", "queued", "processing"];

const formatWhen = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      });
};

/**
 * GET /assistants/:id/installation-status, turned into the checklist rows.
 *
 * Each check arrives as its own object with a boolean and some evidence
 * alongside it (when it was seen, which domain, why the crawl failed), so the
 * detail line quotes the API rather than guessing.
 */
const checksFrom = (status, assistant) => {
  const crawlState = status?.websiteRead?.status || assistant?.status || "";
  const crawling = PENDING_STATUSES.includes(crawlState);
  const crawlError = status?.websiteRead?.lastCrawlError;
  const crawledAt = formatWhen(status?.websiteRead?.lastCrawledAt);
  const seenAt = formatWhen(status?.widgetLoaded?.firstSeenAt);
  const checkedAt = formatWhen(status?.scriptDetected?.checkedAt);
  const domain = status?.domainVerified?.domain || assistant?.allowedDomain;

  return [
    {
      title: "Website read",
      detail: crawling
        ? "Still crawling your pages…"
        : crawlState === "ready"
          ? crawledAt
            ? `Last crawled ${crawledAt}`
            : "Your pages have been read"
          : crawlError || "The crawl hasn't finished",
      done: crawlState === "ready",
      busy: crawling,
    },
    {
      title: "Script detected",
      detail: status?.scriptDetected?.detected
        ? checkedAt
          ? `Found on your site, checked ${checkedAt}`
          : "Found on your site"
        : checkedAt
          ? `Not found yet, last checked ${checkedAt}`
          : "Add the embed snippet to your site",
      done: Boolean(status?.scriptDetected?.detected),
    },
    {
      title: "Widget loaded",
      detail: status?.widgetLoaded?.loaded
        ? seenAt
          ? `First seen ${seenAt}`
          : "The widget has loaded for a visitor"
        : "Waiting for the widget to load on a page",
      done: Boolean(status?.widgetLoaded?.loaded),
    },
    {
      title: "Domain allowed",
      detail: status?.domainVerified?.verified
        ? `${domain} is verified`
        : domain
          ? `${domain} isn't verified yet`
          : "No domain set yet",
      done: Boolean(status?.domainVerified?.verified),
    },
    {
      title: "SSL secure",
      detail: status?.sslSecure?.secure
        ? `${hostOf(assistant?.sourceUrl) || "Your site"} is served over HTTPS`
        : "Your site isn't using HTTPS",
      done: Boolean(status?.sslSecure?.secure),
    },
  ];
};

export default function InstallationStatusCard({ assistant, onRecheck }) {
  const assistantId = assistant?.id;
  const dispatch = useDispatch();

  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  // Separates "no answer yet" from "answered, nothing is connected" - the two
  // look identical in the checklist but only one should claim to be a result.
  const [loaded, setLoaded] = useState(false);

  const check = useCallback(async () => {
    if (!assistantId) return;

    setChecking(true);
    const result = await dispatch(getInstallationStatus(assistantId));
    setChecking(false);
    setLoaded(true);

    if (!result?.success) {
      setError(result?.message || "We couldn't check your installation.");
      return;
    }

    setError("");
    setStatus(result.status);
  }, [assistantId, dispatch]);

  useEffect(() => {
    check();
  }, [check]);

  const handleRecheck = () => {
    check();
    // The record carries the domain and crawl source shown alongside the
    // checks, so it is worth refreshing at the same time.
    onRecheck?.();
  };

  const checks = checksFrom(status, assistant);
  const doneCount = checks.filter((item) => item.done).length;
  // The API's own verdict when it has answered; the count is only a fallback.
  const connected = loaded ? Boolean(status?.connected) : doneCount === checks.length;

  return (
    <section className="dash-card">
      <CardHeading
        step={3}
        tone="green"
        title="Your installation status"
        subtitle={
          connected
            ? "Hostor is live on your website."
            : "We'll check everything for you. A few things still need finishing."
        }
        aside={
          <span
            className={`bill-badge${connected ? " bill-badge--active" : " bill-badge--pending"}`}
          >
            {connected && <CheckOnlyIcon />}
            {connected ? "Connected" : `${doneCount} / ${checks.length} done`}
          </span>
        }
      />

      {/* How far along, at a glance - the count above says it in words, and a
          bar says it before the words are read. */}
      <div
        className="ist-progress"
        role="progressbar"
        aria-valuenow={doneCount}
        aria-valuemin={0}
        aria-valuemax={checks.length}
        aria-label="Installation checks passed"
      >
        <span
          style={{
            width: `${checks.length ? (doneCount / checks.length) * 100 : 0}%`,
          }}
        />
      </div>

      {error && <p className="auth-error">{error}</p>}

      <ul
        className={`onboarding__checklist ist-status-list${checking ? " is-checking" : ""}`}
        aria-busy={checking}
      >
        {/* is-done is what turns the tick green: the checklist's base rule
            greys every span in the row, the icon's wrapper included. */}
        {checks.map((item) => (
          <li key={item.title} className={item.done ? "is-done" : "is-pending"}>
            <span className="onboarding__check-icon">
              {/* Every row spins while a check is in flight, so the card
                  reads as re-testing rather than sitting on a stale answer. */}
              {checking || item.busy ? (
                <span className="install-status-spinner" aria-hidden="true" />
              ) : item.done ? (
                <CheckIcon />
              ) : (
                <span className="install-verify-pending" aria-hidden="true" />
              )}
            </span>
            <div>
              <strong>{item.title}</strong>
              <span>{item.detail}</span>
            </div>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="onboarding__btn onboarding__btn--secondary ist-recheck-btn marginTop10"
        onClick={handleRecheck}
        disabled={checking || !assistantId}
      >
        <RefreshIcon className={checking ? "is-spinning" : ""} />
        {checking ? "Checking..." : "Recheck installation"}
      </button>
    </section>
  );
}
